import type React from "react";
import { Box, Typography, Paper } from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import type { Proposal } from "../types/proposal.types";
import DynamicThumbnailCard from "../../stream-management/components/thumbnail/ThumbnailCard";

interface WhatsAppPreviewPageProps {
  proposal: Proposal;
  stream_id: number;
}

const WhatsAppPreviewPage: React.FC<WhatsAppPreviewPageProps> = ({
  proposal,
  stream_id,
}) => {
  const formatDate = (dateString?: string) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const deceasedFullName = `${
    proposal.booking?.deceased?.[0]?.first_name || proposal.deceasedName || ""
  } ${proposal.booking?.deceased?.[0]?.surname || ""}`.trim();

  const serviceDate = formatDate(
    proposal.booking?.services?.[0]?.date_of_service,
  );

  const formatTime = (time?: string) => {
    if (!time) return "";
    return time.split(":").slice(0, 2).join(":");
  };

  const serviceTime = formatTime(proposal.booking?.services?.[0]?.start_time);

  const venueName = proposal.booking?.services?.[0]?.venue?.name || "";
  const venueAddress =
    proposal.booking?.services?.[0]?.venue?.address_one || "";
  const venueCity = proposal.booking?.services?.[0]?.venue?.city || "";

  const memorialLink = `https://muzukuru.com/memorial-page/${proposal.id}/${deceasedFullName
    .toLowerCase()
    .replace(/\s+/g, "-")}`;

  return (
    <Box
      id="whatsapp-page"
      sx={{
        p: 6,
        width: "816px",
        height: "1000px",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <WhatsAppIcon sx={{ fontSize: 80, color: "#25D366", mb: 2 }} />
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, mb: 2, textTransform: "uppercase" }}
        >
          MANY OF THE FAMILIES WE WORK WITH USE
          <br />
          WHATSAPP AS A PRIMARY SOURCE OF COMMUNICATIONS
        </Typography>
        <Typography variant="h6" sx={{ color: "#666" }}>
          OUR TEAM WILL CREATE A MESSAGE WHICH YOU CAN USE – YOU CAN ALSO EDIT
          IT TO YOUR OWN PREFERENCE
        </Typography>
      </Box>

      {/* Phone + Arrows Container */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          position: "relative",
        }}
      >
        {/* THUMBNAIL ARROW */}
        <Box
          sx={{
            position: "absolute",
            left: 60,
            top: 80,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 20 }}>
            THUMBNAIL
          </Typography>
          <Box
            sx={{
              width: 30,
              height: 3,
              bgcolor: "#000",
              position: "relative",
              "&::after": {
                content: '""',
                position: "absolute",
                right: 0,
                top: "-4px",
                borderLeft: "8px solid #000",
                borderTop: "5px solid transparent",
                borderBottom: "5px solid transparent",
              },
            }}
          />
        </Box>

        {/* VENUE ARROW */}
        <Box
          sx={{
            position: "absolute",
            left: 80,
            top: 350,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 20 }}>VENUE</Typography>
          <Box
            sx={{
              width: 45,
              height: 3,
              bgcolor: "#000",
              position: "relative",
              "&::after": {
                content: '""',
                position: "absolute",
                right: 0,
                top: "-4px",
                borderLeft: "8px solid #000",
                borderTop: "5px solid transparent",
                borderBottom: "5px solid transparent",
              },
            }}
          />
        </Box>

        {/* LINK ARROW */}
        <Box
          sx={{
            position: "absolute",
            left: 60,
            top: 430,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 20 }}>LINK</Typography>
          <Box
            sx={{
              width: 90,
              height: 3,
              bgcolor: "#000",
              position: "relative",
              "&::after": {
                content: '""',
                position: "absolute",
                right: 0,
                top: "-4px",
                borderLeft: "8px solid #000",
                borderTop: "5px solid transparent",
                borderBottom: "5px solid transparent",
              },
            }}
          />
        </Box>

        {/* PHONE */}
        <Paper
          sx={{
            width: 350,
            height: 620,
            borderRadius: "30px",
            border: "12px solid #1a1a1a",
            backgroundColor: "#e5ddd5",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* WhatsApp Header */}
          <Box
            sx={{
              backgroundColor: "#075E54",
              color: "white",
              px: 1.5,
              py: 1,
              fontSize: "12px",
            }}
          >
            CLIIMZ | {deceasedFullName}
          </Box>

          {/* Message */}
          <Box sx={{ p: 1.25 }}>
            <Box sx={{ backgroundColor: "white", borderRadius: 2, p: 1.25 }}>
              {/* Thumbnail */}
              <Box sx={{ height: 200, overflow: "hidden", mb: 1 }}>
                <Box
                  sx={{
                    transform: "scale(0.75)",
                    transformOrigin: "top left",
                    width: "133%",
                  }}
                >
                  <DynamicThumbnailCard streamId={stream_id} />
                </Box>
              </Box>

              <Typography sx={{ fontSize: "12.5px", mb: 0.5 }}>
                Dear Family & Friends,
              </Typography>

              <Typography sx={{ fontSize: "12.5px", mb: 0.5 }}>
                You are invited to virtually celebrate the life of{" "}
                <strong>{deceasedFullName}</strong>.
              </Typography>

              <Typography
                sx={{ fontSize: "12.5px", fontWeight: 600, mb: 0.25 }}
              >
                Memorial Service Details:
              </Typography>

              <Typography sx={{ fontSize: "12.5px" }}>
                <strong>Date:</strong> {serviceDate}
              </Typography>
              <Typography sx={{ fontSize: "12.5px" }}>
                <strong>Time:</strong> {serviceTime}
              </Typography>
              <Typography sx={{ fontSize: "12.5px" }}>
                <strong>Venue:</strong> {venueName}
              </Typography>
              <Typography sx={{ fontSize: "12.5px", mb: 0.5 }}>
                <strong>Address:</strong> {venueAddress}
                {venueCity && `, ${venueCity}`}
              </Typography>

              <Typography
                sx={{ fontSize: "12.5px", fontWeight: 600, mb: 0.25 }}
              >
                Link details:
              </Typography>

              <Typography
                sx={{
                  fontSize: "12.5px",
                  color: "#1976d2",
                  wordBreak: "break-all",
                }}
              >
                {memorialLink}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default WhatsAppPreviewPage;
